<template>
    <div class="main">
        <div class="fd-list-header">
            <h1 class="fd-list-header__brand">Redhill Food Distribution</h1>
            <p class="subtitle is-6 has-text-grey mt-1">Choose a date to volunteer</p>
        </div>

        <base-spinner v-if="isLoading"></base-spinner>

        <div v-else-if="error" class="notification is-danger is-light">
            <p>{{ error }}</p>
        </div>

        <div v-if="liveFD" class="notification is-primary is-light has-text-centered">
            <p class="has-text-weight-bold">Today's Food Distribution is live!</p>
            <p class="mt-1">Go to your deliveries page to see your team and assignments.</p>
            <router-link v-if="isLoggedIn" to="/" class="button is-primary mt-3">
                View my deliveries
            </router-link>
            <router-link v-else to="/login" class="button is-primary mt-3">
                Log in to view deliveries
            </router-link>
        </div>

        <div v-else-if="fds && fds.length === 0">
            <div class="notification is-info is-light has-text-centered">
                <p>No upcoming Food Distributions are open for sign-up right now.</p>
                <p class="mt-2">Check back soon!</p>
            </div>
            <offer-help-banner></offer-help-banner>
        </div>

        <div v-else>
            <template v-for="fd in fds" :key="fd.id">
                <!-- Full and not registered: non-navigable div -->
                <div v-if="isFull(fd) && !registrations[fd.id]"
                    class="fd-card fd-card--full block"
                >
                    <div class="fd-card__date">{{ formatDate(fd.date) }}</div>
                    <div class="fd-card__meta">
                        <span v-if="fd.sponsor" class="fd-card__sponsor">Sponsored by <strong>{{ fd.sponsor }}</strong></span>
                        <span class="fd-card__full-badge">
                            <i class="fas fa-ban"></i> Full
                        </span>
                    </div>
                </div>

                <!-- Available or already registered: navigable -->
                <router-link v-else
                    :to="'/volunteer/' + fd.id"
                    class="fd-card block"
                    :class="{ 'fd-card--registered': registrations[fd.id] }"
                >
                    <div class="fd-card__date">{{ formatDate(fd.date) }}</div>
                    <div class="fd-card__meta">
                        <span v-if="registrations[fd.id]" class="fd-card__registered-badge">
                            <i class="fas fa-circle-check"></i> Registered
                        </span>
                        <span v-else-if="fd.sponsor" class="fd-card__sponsor">Sponsored by <strong>{{ fd.sponsor }}</strong></span>
                        <span class="fd-card__arrow">
                            <i class="fas fa-chevron-right"></i>
                        </span>
                    </div>
                </router-link>
            </template>
        </div>

        <div v-if="!isLoggedIn" class="has-text-centered mt-5">
            <p class="has-text-grey is-size-7">Already have an account?
                <router-link to="/login">Log in</router-link>
            </p>
        </div>
    </div>
</template>

<script>
import { auth } from '@/firebase/firebaseInit';
import OfferHelpBanner from '@/components/volunteer/OfferHelpBanner.vue';

export default {
    components: { OfferHelpBanner },
    data() {
        return {
            isLoading: false,
            error: null,
            registrations: {}
        }
    },
    computed: {
        fds() {
            return this.$store.getters['fd/getFDs'];
        },
        isLoggedIn() {
            return !!auth.currentUser;
        },
        liveFD() {
            return this.$store.getters['fd/getLiveFD'];
        }
    },
    async mounted() {
        this.isLoading = true;
        try {
            await Promise.all([
                this.$store.dispatch('fd/fetchOpenFDs'),
                this.$store.dispatch('fd/fetchLiveFD'),
            ]);
            if (auth.currentUser && this.fds && this.fds.length) {
                const results = await Promise.all(
                    this.fds.map(fd =>
                        this.$store.dispatch('volunteer/getUserRegistration', {
                            fdID: fd.id,
                            userID: auth.currentUser.uid
                        }).then(data => [fd.id, data])
                    )
                );
                this.registrations = Object.fromEntries(results.filter(([, data]) => data !== null));
            }
        } catch (err) {
            this.error = err.message || 'Failed to load upcoming events.';
        }
        this.isLoading = false;
    },
    methods: {
        isFull(fd) {
            const redhillFull =
                (fd.volunteers_drivers_remaining ?? 0) <= 0 &&
                (fd.volunteers_others_remaining ?? 0) <= 0 &&
                (fd.sponsor_volunteers_drivers_remaining ?? 0) <= 0 &&
                (fd.sponsor_volunteers_others_remaining ?? 0) <= 0;
            const tbrFull =
                !(fd.tbr_volunteers_required > 0 && fd.tbr_volunteers_remaining > 0) &&
                !(fd.sponsor_tbr_volunteers_required > 0 && fd.sponsor_tbr_volunteers_remaining > 0);
            return redhillFull && tbrFull;
        },
        formatDate(timestamp) {
            if (!timestamp) return '';
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'Asia/Singapore'
            });
        }
    }
}
</script>

<style scoped>
.fd-list-header {
    text-align: center;
    padding: 1.25rem 0 0.5rem;
}

.fd-list-header__brand {
    font-size: 1.6rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
}

.fd-card {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 1.1rem 1.25rem;
    border-radius: 14px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    box-shadow: 0 4px 14px -8px rgba(15, 23, 42, 0.18);
    text-decoration: none;
    transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.fd-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px -8px rgba(15, 23, 42, 0.22);
    border-color: #14b8a6;
}

.fd-card__date {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
}

.fd-card__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.fd-card__sponsor {
    font-size: 0.9rem;
    color: #6b7280;
}

.fd-card__arrow {
    color: #14b8a6;
    margin-left: auto;
}

.fd-card--registered {
    border-color: #6ee7b7;
    background: #f0fdf4;
}

.fd-card--registered:hover {
    border-color: #10b981;
}

.fd-card--full {
    opacity: 0.6;
    cursor: default;
    background: #f9fafb;
    border-color: #e5e7eb;
}

.fd-card--full:hover {
    transform: none;
    box-shadow: 0 4px 14px -8px rgba(15, 23, 42, 0.18);
    border-color: #e5e7eb;
}

.fd-card__full-badge {
    font-size: 0.82rem;
    font-weight: 700;
    color: #9ca3af;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin-left: auto;
}

.fd-card__registered-badge {
    font-size: 0.82rem;
    font-weight: 700;
    color: #065f46;
    display: flex;
    align-items: center;
    gap: 0.3rem;
}
</style>
